import { CalendarClock, GitBranch, Rocket, Siren, Tag, Train } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { TONE_BADGE, TONE_DOT, type Tone } from "../../lib/status";
import type { ReleaseKind, ReleaseStatus, ReleaseSummary, ReleaseView } from "../../types";
import { Badge } from "../ui/badge";

export const RELEASE_STATUS_TONE: Record<ReleaseStatus, Tone> = {
  open: "neutral",
  shipping: "sky",
  published: "sky",
  deploying: "sky",
  deployed: "emerald",
  failed: "red",
  cancelled: "neutral",
};

export const RELEASE_STATUS_LABEL: Record<ReleaseStatus, string> = {
  open: "Open",
  shipping: "Shipping",
  published: "Published",
  deploying: "Deploying",
  deployed: "Deployed",
  failed: "Failed",
  cancelled: "Cancelled",
};

const PULSING: ReadonlySet<ReleaseStatus> = new Set(["shipping", "published", "deploying"]);

export const RELEASE_KIND_LABEL: Record<ReleaseKind, string> = {
  standard: "Standard",
  urgent: "Urgent",
  scheduled: "Scheduled",
  manual: "Manual (GitHub)",
};

export function ReleaseKindIcon({ kind, className }: { kind: ReleaseKind; className?: string }) {
  const cls = cn("size-3.5 shrink-0", className);
  switch (kind) {
    case "urgent":
      return <Siren className={cls} />;
    case "scheduled":
      return <CalendarClock className={cls} />;
    case "manual":
      return <Tag className={cls} />;
    default:
      return <Train className={cls} />;
  }
}

export function ReleaseStatusBadge({ status, className }: { status: ReleaseStatus; className?: string }) {
  const tone = RELEASE_STATUS_TONE[status];
  return (
    <Badge variant="outline" className={cn("gap-1.5", TONE_BADGE[tone], className)}>
      <span className="relative inline-flex size-2 shrink-0" aria-hidden="true">
        {PULSING.has(status) && (
          <span
            className={cn(
              "absolute inline-flex size-full animate-ping rounded-full opacity-60",
              TONE_DOT[tone],
            )}
          />
        )}
        <span className={cn("relative inline-flex size-2 rounded-full", TONE_DOT[tone])} />
      </span>
      {RELEASE_STATUS_LABEL[status]}
    </Badge>
  );
}

export function ReleaseKindBadge({ kind, className }: { kind: ReleaseKind; className?: string }) {
  return (
    <Badge variant="outline" className={cn("gap-1.5", className)}>
      <ReleaseKindIcon kind={kind} />
      {RELEASE_KIND_LABEL[kind]}
    </Badge>
  );
}

/** The one-line title of a release: its tag once it has one, otherwise its name. */
export function releaseTitle(release: Pick<ReleaseView, "tag" | "name" | "status">): string {
  if (release.tag && release.status !== "open" && release.status !== "shipping") return release.tag;
  return release.name;
}

export function formatSchedule(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-AU", {
    timeZone: "Australia/Sydney",
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Compact label for the issue sidebar, e.g. "Next release", "Scheduled Mon 20 Oct 16:00", "Shipped in v1.4.0". */
export function releaseSummaryLabel(release: ReleaseSummary): { text: string; icon: ReactNode } {
  const icon = <ReleaseKindIcon kind={release.kind} />;
  switch (release.status) {
    case "deployed":
      return {
        text: `Shipped in ${release.tag ?? release.name}`,
        icon: <Rocket className="size-3.5 shrink-0" />,
      };
    case "published":
    case "deploying":
      return {
        text: `${release.status === "deploying" ? "Deploying" : "Published"} ${release.tag ?? release.name}`,
        icon,
      };
    case "failed":
      return { text: `${release.tag ?? release.name} failed`, icon };
    case "cancelled":
      return { text: `${release.name} (cancelled)`, icon };
    case "shipping":
      return { text: `Shipping ${release.name}`, icon };
    default:
      if (release.kind === "scheduled")
        return { text: `Scheduled ${formatSchedule(release.scheduledFor)}`, icon };
      if (release.kind === "urgent") {
        return {
          text: release.branch.startsWith("release/") ? `Urgent · ${release.branch}` : "Urgent release",
          icon: release.branch.startsWith("release/") ? <GitBranch className="size-3.5 shrink-0" /> : icon,
        };
      }
      return { text: release.name, icon };
  }
}
